<template>
  <div v-cloak>
    <div style="display: flex; justify-content: flex-end; width: 100%;">
      <div class="ui compact menu">
        <div class="link item" :class="{ 'active' : currentView === 'overview' }" @click="currentView = 'overview'">
          {{ $t('pages.bookings.reserved_bookings.labels.overview') }}
        </div>
        <div class="link item" :class="{ 'active' : currentView === 'calendar' }" @click="currentView = 'calendar'">
          {{ $t('pages.bookings.reserved_bookings.labels.calendar') }}
        </div>
      </div>
    </div>

    <template v-if="currentView === 'overview'">
      <table class="ui celled table reserved-booking-table">
        <thead>
        <tr>
          <th v-for="(header, key) in headers" :key="key" style="cursor: pointer" @click="applySort(header)">
            {{ $t('pages.bookings.reserved_bookings.table.' + key) }}
            <i class="angle icon" v-if="currentSortField === header"
               :class="{ down: currentSortType === 'ASC', up: currentSortType === 'DESC' }"/>
          </th>
          <th>{{ $t('pages.bookings.reserved_bookings.table.actions') }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="booking in sortedBookings" :key="booking.id">
          <td>
            <div class="booking-col">
              <span class="booking-col-name">Product:</span>
              <span class="booking-col-value">
                {{ booking.booking.product.name }}
                <span v-if="booking.has_guest" class="guest-booking-indicator"
                      :title="$t('modals.newbookingmodal.guest.included')">
                  <i class="users icon"></i>
                </span>
              </span>
            </div>
          </td>
          <td>
            <div class="booking-col">
              <span class="booking-col-name">Start time:</span>
              <span class="booking-col-value">{{ booking.start_time | date('Do MMM, YYYY HH:mm:ss') }}</span>
            </div>
          </td>
          <td>
            <div class="booking-col">
              <span class="booking-col-name">End time:</span>
              <span class="booking-col-value">{{ booking.end_time | date('Do MMM, YYYY HH:mm:ss') }}</span>
            </div>
          </td>
          <td>
            <div class="booking-col">
              <span class="booking-col-name">Status:</span>
              <span class="booking-col-value">
                <div class="ui label"
                     :class="[{'green' : booking.status == 'active'}, {'red' : booking.status == 'deleted'}]">
                  {{ $t('globals.status.' + booking.status) }}
                </div>
              </span>
            </div>
          </td>
          <td>
            <div class="booking-col">
              <span class="booking-col-name">Price:</span>
              <span class="booking-col-value">
                <template v-if="totalPrice(booking) > 0">
                  {{ safeObjectGet(booking, 'price') | money }}
                </template>
                <template v-else>
                  {{ $t('pages.bookings.reserved_bookings.labels.free') }}
                </template>
                <template
                  v-if="(safeObjectGet(booking, 'startup_fee') === 'requested' || safeObjectGet(booking, 'startup_fee') === 'completed') && safeObjectGet(booking, 'status') !== 'active'">
                    <br>+ {{ safeObjectGet(booking, 'booking.product.fee_amount') | money }}
                  </template>
              </span>
            </div>
          </td>
          <td>
            <div class="booking-col">
              <span class="booking-col-name">Actions:</span>
              <span class="booking-col-value">
                <div class="ui blue mini button"
                     v-if="(safeObjectGet(booking, 'status') === 'unset' || safeObjectGet(booking, 'status') === 'activating') && currentTime < safeObjectGet(booking, 'start_time')"
                     @click="payForBooking(booking)">
              <span v-if="booking.is_free && !booking.has_guest">
                {{ $t('pages.bookings.reserved_bookings.labels.activate') }}
              </span>
              <span v-else>
                {{ $t('pages.bookings.reserved_bookings.labels.pay') }}
              </span>
						</div>
                <div class="ui blue mini button"
                     v-if="safeObjectGet(booking, 'status') === 'active' && safeObjectGet(booking, 'payment_method') !== 'mobile_pay' && safeObjectGet(booking, 'payment_method') !== 'vipps' && currentTime < safeObjectGet(booking, 'start_time') && totalPrice(booking) > 0"
                     @click="payForBooking(booking, true)">
                  {{ $t('globals.buttons.edit') }}
                </div>
                <div class="ui grey mini button" v-if="showCancelButton(booking)" @click="confirmDeletion(booking.id)">
							{{ $t('globals.buttons.cancel_booking') }}
						</div>
              </span>
            </div>
          </td>
        </tr>
        </tbody>
        <tfoot class="full-width">
        </tfoot>
      </table>

      <div class="ui pagination menu" v-if="booked.last_page > 1">
        <a class="item" v-for="amount in booked.last_page" :key="amount"
           :class="{ 'active' : amount === currentPage }"
           @click="currentPage = amount">
          {{ amount }}
        </a>
      </div>
    </template>

    <div class="ui segment" v-if="currentView === 'calendar'">
      <FullCalendar :locale="calendarConfig.locale" defaultView="dayGridMonth" :events="bookingEvents"
                    :plugins="calendarPlugins" :event-render="customCalendarRender"/>
    </div>

    <modal ref="modal" :active="deleteBookingModal" :icons="'big calendar alternate outline'"
           :title="$t('pages.bookings.validation.delete_booking.title')" :popout="true"
           @acceptmodal="deleteBooking(deleteBookingId)" @denymodal="cancelDeletion()">
      <h4>{{ $t('pages.bookings.validation.delete_booking.header') }}</h4>
      <span v-html="$t('pages.bookings.validation.delete_booking.text')"/>

      <div slot="box-actions">
        <div class="ui red basic circular button" @click="$refs.modal.denyModal()">
          {{ $t('globals.buttons.no') }}
        </div>
        <div class="ui blue circular button" @click="$refs.modal.acceptModal()">
          {{ $t('globals.buttons.yes') }}
        </div>
      </div>
    </modal>

    <modal ref="modal" :active="showUpdateBookingNotice" :title="$t('modals.payment_system_update.title')"
           icons="big alarm" @acceptmodal="openCheckoutPage" @denymodal="closeUpdateModal">
      <h4>{{
          $t('modals.payment_system_update.header', {
            firstname: this.$auth.user().firstname,
            surname: this.$auth.user().surname
          })
        }}</h4>
      <span
        v-html="$t('modals.payment_system_update.body', {container: $t('globals.container.booked'), product_name: this.bookedActiveProductName})"></span>
    </modal>

    <div class="ui">
      <new-booking-modal :active="showPaymentModal" :bookingid="selectedBooking.booking.id" :selectedPlace="selectedPlace"
                         :update-booking="selectedBooking" v-if="selectedBooking !== null"
                         @onClose="selectedBooking = null" :update="true" @open-image="handleOpenImage" />

      <winter-place-modal v-if="showWinterPlaceModal" :selected-map-element="selectedSpot"
                          @close="showWinterPlaceModal = false" @polygon-selected="handlePolygonSelected"/>
    </div>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import da from '@fullcalendar/core/locales/da'
import en from '@fullcalendar/core/locales/en-gb'
import _ from 'lodash'
import moment from 'moment'

export default {
  name: 'ReservedBookings',

  components: {
    FullCalendar
  },

  data() {
    return {
      currentSortField: '',
      currentSortType: 'ASC',
      headers: {
        product: 'booking.product.name',
        start_time: 'start_time',
        end_time: 'end_time',
        status: 'status',
        price: 'price,'
      },
      booked: {
        data: []
      },
      currentPage: 1,
      fetchError: null,
      deleteBookingModal: false,
      deleteBookingId: null,
      editingBooking: false,
      showUpdateBookingNotice: false,
      bookedActiveProductName: '',
      bookedActiveProductId: null,
      showPaymentModal: false,
      selectedBookingPricing: null,
      selectedBooking: null,
      showWinterPlaceModal: false,
      selectedBookingId: null,
      selectedPlace: null,
      selectedSpot: {},

      calendarPlugins: [dayGridPlugin],
      calendarConfig: {
        locale: (window.localStorage.language === 'da') ? da : en
      },
      currentView: 'overview'
    }
  },

  watch: {
    activeMerchant() {
      this.fetchBooked()
    },

    currentPage: function () {
      this.fetchBooked()
    }
  },

  mounted() {
    this.fetchBooked()

    this.$events.$on('booking-reserved', (data) => {
      this.fetchBooked()
    })
  },

  methods: {
    handleOpenImage(payload) {
      this.selectedSpot = payload;
      this.showWinterPlaceModal = true;
    },

    handlePolygonSelected(place) {
      this.selectedPlace = place;
      this.showWinterPlaceModal = false;
    },

    customCalendarRender(info) {
      const booking = info.event.extendedProps._initial
      let price = 'FREE'
      if (this.safeObjectGet(booking, 'price') > 0) {
        price = booking.price
      }

      if (booking.has_guest) {
        const title = info.el.querySelector('.fc-title')

        if (title) {
          const guestIcon = document.createElement('i')
          guestIcon.className = 'users icon calendar-guest-booking-indicator'
          guestIcon.title = this.$t('modals.newbookingmodal.guest.included')
          title.insertBefore(guestIcon, title.firstChild)
        }
      }

      const i = new Tooltip(info.el, {
        title: `Name: ${info.event.title} \n Price: ${price} \n Status: ${booking.status} \n Time: ${moment(info.event.start).format('HH:mm')}-${moment(info.event.end).format('HH:mm')}`,
        placement: 'top',
        trigger: 'hover',
        container: 'body'
      })
    },
    applySort(field) {
      if (this.currentSortField === field) {
        if (this.currentSortType === 'ASC') {
          this.currentSortType = 'DESC'
        } else {
          this.currentSortType = 'ASC'
        }
      } else {
        this.currentSortField = field
        this.currentSortType = 'ASC'
      }
    },

    activateSubscription(bookedID) {
      return new Promise((resolve, reject) => {
        this.axios.put(`/booked/${bookedID}/activate`)
          .then(({data}) => {
            resolve(data)
            let message
            this.fetchBooked()

            if (this.editingBooking) {
              message = this.$t('pages.bookings.validation.success.booking_edited')
            } else {
              message = this.$t('pages.bookings.validation.success.booking_paid')
            }

            this.$events.$emit('alert', {
              header: this.$t('globals.validation.success.success'),
              message: message,
              type: 'success',
              align: 'right'
            })
          })
          .catch(error => {
            reject(error)
          })
      })
    },

    fetchBooked(page = this.currentPage, perPage = 10) {
      return new Promise((resolve, reject) => {
        this.axios.get(`merchants/${this.activeMerchant.id}/bookings/booked`, {
          params: {
            page: page,
            perPage: 10,
            status: ['unset', 'active', 'activating']
          }
        })
          .then(({data}) => {
            this.booked = data
            this.fetchError = null

            this.booked.data.forEach((booking, index) => {
              if (booking.status === 'active' && booking.payment_time !== null && moment(booking.payment_time).format('YYYY-MM-DD HH:mm:ss') > this.currentTime) {
                if (booking.nets_details !== null && booking.nets_details.payment_id === null) {
                  this.showUpdateBookingNotice = true
                  this.bookedActiveProductId = booking.id
                  this.bookedActiveProductName = booking.booking.product.name
                }
              }
            })

            resolve(data)
          }, error => {
            this.booked = []
            this.fetchError = error.response.data

            reject(error)
          }).catch()
      })
    },

    getBookedById(bookedId) {
      this.axios.get(`booked/${bookedId}`).then()
    },

    totalPrice(booking) {
      return +this.safeObjectGet(booking, 'price')
    },

    openCheckoutPage() {
      this.$router.push({
        name: 'checkout',
        params: {containerId: this.bookedActiveProductId},
        query: {container: 'booked'}
      })
    },

    closeUpdateModal() {
      this.showUpdateSubscriptionNotice = false
    },

    payForBooking(booking, editing = false) {
      this.selectedBookingPricing = this.safeObjectGet(booking, 'pricing')
      this.selectedBooking = booking

      this.editingBooking = editing

      if (this.selectedBookingPricing && this.selectedBooking) {
        this.$nextTick(() => {
          this.showPaymentModal = !this.showPaymentModal
        })
      } else {
        this.$events.$emit('alert', {
          header: this.$t('globals.validation.errors.error'),
          message: 'Payment plan is not selected. Please contact merchant',
          type: 'error',
          align: 'right'
        })
      }
    },

    confirmDeletion(bookingId) {
      this.deleteBookingModal = !this.deleteBookingModal
      this.deleteBookingId = bookingId
    },

    deleteBooking(bookingId) {
      if (bookingId) {
        this.axios.delete(`booked/${bookingId}`)
          .then(response => {
            if (response.data.status === false) {
              this.$events.$emit('alert', {
                header: this.$t('globals.validation.errors.error'),
                message: response.data.message,
                type: 'error',
                align: 'right'
              })
            } else {
              this.fetchBooked()
                .then(data => {
                  if (this.currentPage > data.last_page) {
                    this.currentPage = data.last_page
                    this.fetchBooked(data.last_page)
                  }
                })
              this.$events.$emit('alert', {
                header: this.$t('globals.validation.success.success'),
                message: this.$t('pages.bookings.validation.success.booking_deleted'),
                type: 'success',
                align: 'right'
              })
            }
          })
          .catch((error) => {
            let errorMessage = ''
            try {
              errorMessage = Object.values(error.response.data.errors).join(';')
              this.$events.$emit('alert', {
                header: this.$t('globals.validation.errors.error'),
                message: errorMessage,
                type: 'error',
                align: 'right'
              })
            } catch (e) {
              errorMessage = this.$t('globals.validation.errors.try_again_later')
              this.$events.$emit('alert', {
                header: this.$t('globals.validation.errors.something_went_wrong'),
                message: errorMessage,
                type: 'error',
                align: 'right'
              })
            }
          })
      }
    },

    showCancelButton (booking) {
      if (booking.status !== 'active') {
        return true
      }

      return booking.startup_fee !== 'completed'
    },

    cancelDeletion() {
      this.deleteBookingId = null
    }
  },

  computed: {
    sortedBookings() {
      if (this.currentSortField) {
        const sorted = _.sortBy(this.booked.data, this.currentSortField)
        return this.currentSortType === 'ASC' ? sorted : sorted.reverse()
      }
      return this.booked.data
    },

    activeMerchant() {
      return this.$store.getters.activeMerchant
    },

    currentTime() {
      return moment().format('YYYY-MM-DD HH:mm:ss')
    },

    bookingEvents() {
      let events = []

      this.booked.data.forEach((value, index) => {
        if (value.status === 'active') {
          events.push(
            {
              id: value.id,
              title: value.booking.product.name,
              start: value.start_time,
              end: value.end_time,
              _initial: value
            }
          )
        }
      })
      return events
    }
  },

  beforeDestroy() {
    this.$events.$off('booking-reserved')
  }
}
</script>
<style lang="scss">
.popper,
.tooltip {
  position: absolute;
  z-index: 9999;
  background: #FFC107;
  color: black;
  width: 150px;
  border-radius: 3px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  padding: 10px;
  text-align: center;
}

.style5 .tooltip {
  background: #1E252B;
  color: #FFFFFF;
  max-width: 200px;
  width: auto;
  font-size: .8rem;
  padding: .5em 1em;
}

.popper .popper__arrow,
.tooltip .tooltip-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  margin: 5px;
}

.tooltip .tooltip-arrow,
.popper .popper__arrow {
  border-color: #FFC107;
}

.style5 .tooltip .tooltip-arrow {
  border-color: #1E252B;
}

.popper[x-placement^="top"],
.tooltip[x-placement^="top"] {
  margin-bottom: 5px;
}

.popper[x-placement^="top"] .popper__arrow,
.tooltip[x-placement^="top"] .tooltip-arrow {
  border-width: 5px 5px 0 5px;
  border-left-color: transparent;
  border-right-color: transparent;
  border-bottom-color: transparent;
  bottom: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.popper[x-placement^="bottom"],
.tooltip[x-placement^="bottom"] {
  margin-top: 5px;
}

.tooltip[x-placement^="bottom"] .tooltip-arrow,
.popper[x-placement^="bottom"] .popper__arrow {
  border-width: 0 5px 5px 5px;
  border-left-color: transparent;
  border-right-color: transparent;
  border-top-color: transparent;
  top: -5px;
  left: calc(50% - 5px);
  margin-top: 0;
  margin-bottom: 0;
}

.tooltip[x-placement^="right"],
.popper[x-placement^="right"] {
  margin-left: 5px;
}

.popper[x-placement^="right"] .popper__arrow,
.tooltip[x-placement^="right"] .tooltip-arrow {
  border-width: 5px 5px 5px 0;
  border-left-color: transparent;
  border-top-color: transparent;
  border-bottom-color: transparent;
  left: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.popper[x-placement^="left"],
.tooltip[x-placement^="left"] {
  margin-right: 5px;
}

.popper[x-placement^="left"] .popper__arrow,
.tooltip[x-placement^="left"] .tooltip-arrow {
  border-width: 5px 0 5px 5px;
  border-top-color: transparent;
  border-right-color: transparent;
  border-bottom-color: transparent;
  right: -5px;
  top: calc(50% - 5px);
  margin-left: 0;
  margin-right: 0;
}

.reserved-booking-table {

  &.ui.table:not(.unstackable) {
    thead {
      @media screen and (max-width: 767px) {
        display: none;
      }
    }
  }

  .booking-col {
    display: flex;
    align-items: center;

    @media screen and (min-width: 768px) {
      display: block;
    }

    .booking-col-name {
      min-width: 30%;
      max-width: 30%;
      padding-right: 10px;
      font-weight: 700;

      @media screen and (min-width: 768px) {
        display: none;
      }
    }

    .booking-col-value {
      min-width: 70%;
      max-width: 70%;

      @media screen and (min-width: 768px) {
        min-width: initial;
        max-width: initial;
      }
    }
  }
}

.guest-booking-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 6px;
  border-radius: 50%;
  color: #2185d0;
  background: #e6f3fc;
  cursor: help;
  vertical-align: middle;

  .icon {
    margin: 0;
  }
}

.calendar-guest-booking-indicator {
  margin-right: 4px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/booking/ReservedBookings.vue